import { useEffect, useRef } from "react";
import styled from "@emotion/styled";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import Button from "../Button/Button";
import deleteIcon from "../../Assets/Images/delete-button-icon.png";

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 999;
  opacity: ${({ isOpen }) => (isOpen ? "1" : "0")};
  transition: opacity 0.5s ease-in-out;
  pointer-events: ${({ isOpen }) => (isOpen ? "auto" : "none")};
`;

const ModalContainer = styled.div`
  position: fixed;
  z-index: 999;
  background-color: var(--sidepopup-background);
  box-shadow: ${({ direction }) =>
    direction === "left"
      ? "2px 0 5px rgba(0, 0, 0, 0.1)"
      : direction === "up" || direction === "down"
        ? "0 -2px 5px rgba(0, 0, 0, 0.1)"
        : "-2px 0 5px rgba(0, 0, 0, 0.1)"};
  transition:
    transform 0.6s cubic-bezier(0.4, 0, 0.2, 1),
    opacity 0.5s ease-in-out;
  opacity: ${({ isOpen }) => (isOpen ? "1" : "0")};
  display: flex;
  flex-direction: column;
  overflow: hidden;

  ${({ direction, isOpen, sidePopupWidth, sidePopupHeight }) => {
    switch (direction) {
      case "left":
        return `
          top: 0;
          left: 0;
          height: 100vh;
          width: ${sidePopupWidth || "85%"};
          transform: ${isOpen ? "translateX(0)" : "translateX(-100%)"};
        `;
      case "up":
        return `
          bottom: 0;
          left: 0;
          right: 0;
          width: 100%;
          height: ${sidePopupHeight || "85%"};
          transform: ${isOpen ? "translateY(0)" : "translateY(-100%)"};
        `;
      case "down":
        return `
          bottom: 0;
          left: 0;
          right: 0;
          width: 100%;
          height: ${sidePopupHeight || "85%"};
          transform: ${isOpen ? "translateY(0)" : "translateY(100%)"};
        `;
      case "right":
      default:
        return `
          top: 0;
          right: 0;
          height: 100vh;
          width: ${sidePopupWidth || "85%"};
          transform: ${isOpen ? "translateX(0)" : "translateX(100%)"};
        `;
    }
  }}
`;

const ModalContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${({ contentPadding }) => contentPadding || "20px"};
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 1rem;
  border-bottom: ${({ showHeader, showHeaderBorder }) =>
    showHeader && showHeaderBorder ? "1px solid var(--border__color)" : "none"};
  background-color: ${({ headerBackgroundColor }) =>
    headerBackgroundColor ? headerBackgroundColor : "var(--body__background)"};
`;

const StyledHeaderContainer = styled("div")({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  width: "100%",
  padding: "10px 0px",
});

const StyledCloseContainer = styled("div")({
  display: "flex",
  justifyContent: "flex-end",
  marginRight: "20px",
  marginTop: "15px",
});

const StyledContainerHeader = styled("h2")(({ titleColor }) => ({
  marginBottom: "0px",
  marginTop: "0px",
  color: titleColor ? titleColor : "var(--header__color)",
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "normal",
  lineHeight: "1.2",
  maxHeight: "2.4em",
}));

const StyledSubHeader = styled("h3")(({ subTitleColor }) => ({
  margin: "5px 0 0 0",
  fontSize: "14px",
  fontWeight: "normal",
  color: subTitleColor ? subTitleColor : "var(--text__secondary)",
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "normal",
  lineHeight: "1.2",
  maxHeight: "2.4em",
}));

const StyledHeaderIcon = styled("img")({
  width: "20px",
});

const StyledHeaderIconContainer = styled("div")({
  display: "flex",
  alignItems: "flex-start",
  flexDirection: "column",
  gap: "10px",
});

const StyledHeaderTitleIconContainer = styled("div")({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "10px",
});

const SidePopup = ({
  isOpen,
  onClose,
  children,
  title,
  subTitle = "",
  showHeader = true,
  showHeaderBorder = true,
  sidePopupWidth = "85%",
  sidePopupHeight = "85%",
  headerTitleIcon = false,
  headerTitleIconSrc = "",
  titleIconWidth = "20px",
  titleColor = "",
  subTitleColor = "#62748a",
  headerBackgroundColor = "",
  headerRightContent = null,
  showDeleteButton = false,
  handleDelete = () => {},
  deleteButtonLabel = "Delete",
  direction = "right",
  headerContent = null,
  headerRightComponent,
  contentPadding,
  closeOnOverlayClick = false,
  closeIconDisabled = false,
}) => {
  const popupRef = useRef();

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isOpen]);

  const handleOverlayClick = (e) => {
    if (
      closeOnOverlayClick &&
      popupRef.current &&
      !popupRef.current.contains(e.target)
    ) {
      onClose();
    }
  };

  const handleCloseIconClick = () => {
    if (!closeIconDisabled) {
      onClose();
    }
  };

  const closeIconStyle = {
    cursor: closeIconDisabled ? "not-allowed" : "pointer",
    opacity: closeIconDisabled ? 0.35 : 1,
  };

  return (
    <>
      <ModalOverlay isOpen={isOpen} onClick={handleOverlayClick} />
      <ModalContainer
        isOpen={isOpen}
        sidePopupWidth={sidePopupWidth}
        sidePopupHeight={sidePopupHeight}
        direction={direction}
        ref={popupRef}
      >
        {showHeader ? (
          <ModalHeader
            showHeader={showHeader}
            showHeaderBorder={showHeaderBorder}
            headerBackgroundColor={headerBackgroundColor}
          >
            <StyledHeaderContainer className="sidepopup-header-container">
              {headerContent ? (
                <div
                  className="custom-header-content"
                  style={{ width: "100%" }}
                >
                  {headerContent}
                </div>
              ) : (
                <StyledHeaderIconContainer className="side-popup-header-icon">
                  <StyledHeaderTitleIconContainer className="header-icon-title">
                    {headerTitleIcon && (
                      <StyledHeaderIcon
                        alt="header Title Icon"
                        src={headerTitleIconSrc}
                        style={{ width: titleIconWidth }}
                      />
                    )}
                    <StyledContainerHeader titleColor={titleColor}>
                      {title}
                    </StyledContainerHeader>
                  </StyledHeaderTitleIconContainer>
                  {subTitle && (
                    <StyledSubHeader subTitleColor={subTitleColor}>
                      {subTitle}
                    </StyledSubHeader>
                  )}
                </StyledHeaderIconContainer>
              )}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "16px",
                  marginLeft: "30px",
                }}
              >
                {showDeleteButton && (
                  <Button
                    label={deleteButtonLabel || "Delete"}
                    event={handleDelete}
                    iconSrc={deleteIcon}
                    iconWidth="15px"
                    style={{ marginRight: "10px", padding: "8px 15px" }}
                    backgroundColor="#6860EE"
                    color="#FFFFFF"
                    border="none"
                  />
                )}
                {headerRightComponent || headerRightContent}
                <HighlightOffIcon
                  onClick={handleCloseIconClick}
                  style={closeIconStyle}
                  aria-disabled={closeIconDisabled}
                />
              </div>
            </StyledHeaderContainer>
          </ModalHeader>
        ) : (
          <StyledCloseContainer className="sidepopup-close-container">
            <HighlightOffIcon
              onClick={handleCloseIconClick}
              style={closeIconStyle}
              aria-disabled={closeIconDisabled}
            />
          </StyledCloseContainer>
        )}
        <ModalContent
          className="sidepopup-modal-content"
          contentPadding={contentPadding}
        >
          {children}
        </ModalContent>
      </ModalContainer>
    </>
  );

  // return createPortal(popupMarkup, document.body);
};

export default SidePopup;
